import {
  automationBuilderExamplePromptSchema,
  type AutomationBuilderExamplePrompt,
} from '@reach/shared-types/platforms';
import { useState } from 'react';
import type { ExamplePromptDraft } from './types';

const toDrafts = (prompts: AutomationBuilderExamplePrompt[]): ExamplePromptDraft[] =>
  prompts.map(prompt => ({ id: crypto.randomUUID(), title: prompt.title, prompt: prompt.prompt }));

const promptsKey = (prompts: AutomationBuilderExamplePrompt[]) =>
  JSON.stringify(prompts.map(({ title, prompt }) => ({ title, prompt })));

type ValidationResult = { valid: true; prompts: AutomationBuilderExamplePrompt[] } | { valid: false };

/**
 * Draft state for the example prompts editor. Drafts follow refetched saved prompts until edited,
 * and are dirty while they differ from the prompts they were last synced with.
 */
export const useExamplePromptDrafts = (savedPrompts: AutomationBuilderExamplePrompt[]) => {
  const [drafts, setDrafts] = useState(() => toDrafts(savedPrompts));
  const [showErrors, setShowErrors] = useState(false);

  const savedPromptsKey = promptsKey(savedPrompts);
  const [baselineKey, setBaselineKey] = useState(savedPromptsKey);
  const isDirty = promptsKey(drafts) !== baselineKey;

  const [lastSeenSavedKey, setLastSeenSavedKey] = useState(savedPromptsKey);
  const savedPromptsChanged = savedPromptsKey !== lastSeenSavedKey;
  if (savedPromptsChanged) {
    setLastSeenSavedKey(savedPromptsKey);
    if (!isDirty) {
      setDrafts(toDrafts(savedPrompts));
      setBaselineKey(savedPromptsKey);
    }
  }

  const reset = () => {
    setDrafts(toDrafts(savedPrompts));
    setBaselineKey(savedPromptsKey);
    setShowErrors(false);
  };

  const validateAndShowErrors = (): ValidationResult => {
    const parsedDrafts = drafts.map(draft => automationBuilderExamplePromptSchema.safeParse(draft));
    const validPrompts = parsedDrafts.flatMap(parsed => (parsed.success ? [parsed.data] : []));
    const everyDraftIsValid = validPrompts.length === drafts.length;
    setShowErrors(!everyDraftIsValid);
    return everyDraftIsValid ? { valid: true, prompts: validPrompts } : { valid: false };
  };

  const markSaved = (prompts: AutomationBuilderExamplePrompt[]) => {
    setDrafts(toDrafts(prompts));
    setBaselineKey(promptsKey(prompts));
    setShowErrors(false);
  };

  return { drafts, setDrafts, isDirty, showErrors, reset, validateAndShowErrors, markSaved };
};
